import {
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type { InventoryResource, SourceAccount } from '@repo/api-gw-sdk';
import { useEffect, useMemo, useState } from 'react';

import MainDialog from '@/components/dialogs/mainDialog';
import CopyableCode from '@/components/shared/copyableCode';
import { useDAL } from '@/contexts/useDAL';
import { useSourceAccounts } from '@/contexts/useSourceAccounts';
import { useUser } from '@/contexts/useUser';
import { useWorkspace } from '@/contexts/useWorkspace';

import ErrorDialog from './errorDialog';

import { ReadMoreLink } from '../shared/readMoreLink';

interface SqlVmActionRequiredDialogProps {
  resources: InventoryResource[];
  onClose: () => void;
  onRefreshResources?: () => Promise<void>;
}

export const SqlVmActionRequiredDialog = ({
  resources,
  onClose,
  onRefreshResources,
}: SqlVmActionRequiredDialogProps) => {
  const availableTabs = ['bash', 'terraform'];
  const [selectedTab, setSelectedTab] = useState(availableTabs[0]);
  const { user } = useUser();
  const dal = useDAL();
  const { snackbar } = useWorkspace();
  const eonAccountId = user?.eonAccount?.id || '';
  const { sourceAccounts } = useSourceAccounts();

  // State for the API call
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string>('');

  const onValidateConfiguration = async () => {
    setIsValidating(true);
    setValidationError('');

    try {
      const response = await dal.azure.batchValidateResourceConfiguration(
        resources.map((resource) => resource.id)
      );

      if (response.results.every((result) => result.isValid)) {
        if (onRefreshResources) {
          void onRefreshResources();
        }

        snackbar.showMessage('Configuration validated successfully.');
        onClose();
      } else {
        setValidationError(JSON.stringify(response, null, 2));
      }
    } catch (error) {
      setValidationError(
        error instanceof Error ? error.message : 'An unexpected error occurred'
      );
    } finally {
      setIsValidating(false);
    }
  };

  // Pre-compute all container names and resource data
  const [resourcesData, setResourcesData] = useState<SqlVmResourceData[]>([]);
  const [isLoadingContainerNames, setIsLoadingContainerNames] = useState(true);

  // Compute source accounts for each resource - memoized to prevent infinite re-renders
  const resourcesWithSourceAccounts = useMemo(
    () =>
      resources.map((resource) => ({
        resource,
        sourceAccount: getSourceAccountForResource(resource, sourceAccounts),
      })),
    [resources, sourceAccounts]
  );

  // Create a stable key for each API call to prevent infinite re-renders
  const networkInfoCallKeys = useMemo(
    () =>
      resourcesWithSourceAccounts.map(({ resource, sourceAccount }, index) =>
        sourceAccount?.id
          ? `${sourceAccount.id}-${resource.resourceGroupName}-${resource.resourceName}-${index}`
          : null
      ),
    [resourcesWithSourceAccounts]
  );

  // Make API calls only for resources with valid source accounts (top-level to satisfy Rules of Hooks)
  const networkInfoResults = networkInfoCallKeys.map((key, index) => {
    if (!key) return null;
    const { resource, sourceAccount } = resourcesWithSourceAccounts[index];
    return dal.azure.getSqlVmNetworkInfo({
      cloudAccountId: sourceAccount!.id,
      resourceGroup: resource.resourceGroupName || '',
      vmName: resource.resourceName || '',
    });
  });

  // Memoized projection of network info bodies to use inside effects without violating deps rules
  const resolvedNetworkInfos = useMemo(
    () => networkInfoResults.map((r) => r?.data?.networkInfo || null),
    [networkInfoResults]
  );

  useEffect(() => {
    let cancelled = false;

    const computeResourcesData = async () => {
      try {
        // Ensure all required network info is available before computing data
        const allNetworkInfoAvailable = resourcesWithSourceAccounts.every(
          (_item, index) => !!resolvedNetworkInfos[index]
        );

        if (!allNetworkInfoAvailable) {
          if (!cancelled) {
            setIsLoadingContainerNames(true);
          }
          return;
        }

        const dataPromises = resourcesWithSourceAccounts.map(
          async ({ resource, sourceAccount }, index) => {
            const resourceGroupName = getResourceGroupName(
              resource,
              sourceAccount
            );
            const containerName = await generateStableContainerName(resource);

            // Get network info directly from API response
            const networkInfo = resolvedNetworkInfos[index]!;

            return {
              resource,
              sourceAccount,
              storageAccountResourceGroup: resourceGroupName,
              storageAccountName: await generateStorageAccountName(
                eonAccountId,
                resource.providerAccountId,
                resource.region
              ),
              containerName,
              sqlVmResourceId: buildSqlVmResourceId(resource),
              subnetId: networkInfo.subnetId,
              virtualNetworkName: networkInfo.virtualNetworkName,
              subnetName: networkInfo.subnetName,
            };
          }
        );

        const computedData = await Promise.all(dataPromises);

        if (!cancelled) {
          setResourcesData(computedData);
          setIsLoadingContainerNames(false);
        }
      } catch {
        if (!cancelled) {
          setIsLoadingContainerNames(false);
        }
      }
    };

    void computeResourcesData();

    return () => {
      cancelled = true;
    };
  }, [resourcesWithSourceAccounts, eonAccountId, resolvedNetworkInfos]);

  const loadingTabData: Record<string, TabData> = {
    bash: { title: 'Bash', description: 'Loading...', codeTitle: '', code: '' },
    terraform: {
      title: 'Terraform',
      description: 'Loading...',
      codeTitle: '',
      code: '',
    },
  };

  const tabData = isLoadingContainerNames
    ? loadingTabData
    : getTabData(resourcesData, eonAccountId);

  const dialogTitle =
    resources.length > 1
      ? 'Complete resources configuration'
      : 'Complete resource configuration';

  return (
    <>
      <MainDialog
        isOpen={!validationError}
        isLoading={isLoadingContainerNames}
        header={{
          title: dialogTitle,
          subtitle: (
            <Stack direction='row' spacing={8} className='flex justify-between'>
              <Typography>{tabData[selectedTab].description}</Typography>
              <ToggleButtonGroup
                exclusive
                value={selectedTab}
                onChange={(e, value) => {
                  if (value === null) {
                    // Keep the current selection
                    return;
                  }
                  setSelectedTab(value);
                }}
              >
                <ToggleButton value='bash'>Bash</ToggleButton>
                <ToggleButton value='terraform'>Terraform</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          ),
          fullWidthSubtitle: true,
          alignTitles: true,
          onClose,
        }}
        footer={{
          primaryButton: {
            text: 'Validate Configuration',
            onClick: onValidateConfiguration,
            'data-testid': 'sqlvm-action-required-dialog-validate-button',
          },
          secondaryButton: {
            text: 'Cancel',
            onClick: onClose,
            'data-testid': 'sqlvm-action-required-dialog-cancel-button',
          },
          hideButtons: isLoadingContainerNames,
          submitInProgress: isValidating,
          loadingText: 'Checking configuration...',
        }}
        sx={{
          width: '60%',
          minWidth: '800px',
          maxHeight: '90vh',
          overflowY: 'hidden',
        }}
      >
        <Stack className='px-[24px] py-0'>
          {!isLoadingContainerNames && (
            <CopyableCode
              code={tabData[selectedTab].code}
              title={tabData[selectedTab].codeTitle}
              codeClassName='overflow-y-auto max-h-[30vh]'
              tooltipTitle={
                <Typography
                  variant='body2'
                  component='span'
                  className='text-inherit'
                >
                  Applying this configuration will enable Point-in-Time Restore
                  (PiTR) backups for this SQL VM resource in Eon.
                  <br />
                  <ReadMoreLink
                    text='Learn More'
                    href='/docs/user-guide/backing-up/connect-sources/azure-sources/azure-configure-sql-server-on-azure-vms-for-pitr-backup'
                    className='text-[var(--mui-palette-Tooltip-text)] hover:text-[var(--mui-palette-Tooltip-text)]'
                  />
                </Typography>
              }
            />
          )}
        </Stack>
      </MainDialog>
      <ErrorDialog
        isOpen={!!validationError}
        title='Couldn’t validate configuration'
        description='Check the error details and contact support if needed.'
        onClose={onClose}
        error={validationError}
      />
    </>
  );
};

interface TabData {
  title: string;
  description: string;
  codeTitle: string;
  code: string;
}

const getTabData = (
  resourcesData: SqlVmResourceData[],
  eonAccountId: string
): Record<string, TabData> => {
  const resourcesBySubscriptionAndRegion = resourcesData.reduce<
    Record<string, typeof resourcesData>
  >((acc, data) => {
    const key = `${data.resource.providerAccountId}|${data.resource.region}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(data);
    return acc;
  }, {});

  const resourceCount = resourcesData.length;
  const isMultiple = resourceCount > 1;

  return {
    bash: {
      title: 'Bash',
      description: `Copy and run the following bash commands in order to enable Eon PiTR backups for your SQL VM${isMultiple ? 's' : ''}. Once you're done, click the button below to validate the configuration.`,
      codeTitle: 'main.sh',
      code: generateBashScript(
        resourcesData,
        resourcesBySubscriptionAndRegion,
        eonAccountId
      ),
    },
    terraform: {
      title: 'Terraform',
      description: `Review and deploy the Terraform configuration in your cloud account to enable Eon PiTR backups for your SQL VM${isMultiple ? 's' : ''}. Once you're done, click the button below to validate it.`,
      codeTitle: 'main.tf',
      code: generateTerraformScript(resourcesData, eonAccountId),
    },
  };
};

// Interface definition for SQL VM resources data with computed network information
interface SqlVmResourceData {
  resource: InventoryResource;
  sourceAccount?: SourceAccount;
  storageAccountResourceGroup: string;
  storageAccountName: string;
  containerName: string;
  sqlVmResourceId: string;
  subnetId: string;
  virtualNetworkName: string;
  subnetName: string;
}

// Generate bash script using pre-computed network information from GetSqlVmNetworkInfo API
// This eliminates the need for expensive Azure CLI discovery commands
const generateBashScript = (
  resourcesData: SqlVmResourceData[],
  resourcesBySubscriptionAndRegion: Record<string, typeof resourcesData>,
  eonAccountId: string
): string => {
  const subscriptionRegionKeys = Object.keys(resourcesBySubscriptionAndRegion);

  // Generate arrays for all resources
  const subscriptionIds = resourcesData.map(
    (d) => d.resource.providerAccountId
  );
  const containerNames = resourcesData.map((d) => d.containerName);
  const storageAccountResourceGroups = resourcesData.map(
    (d) => d.storageAccountResourceGroup
  ); // Storage account RGs (from getResourceGroupName)
  const sqlVmResourceGroups = resourcesData.map(
    (d) => d.resource.resourceGroupName || ''
  ); // SQL VM RGs (direct from resource)
  const sqlVmNames = resourcesData.map((d) => d.resource.resourceName);
  const sqlVmResourceIds = resourcesData.map((d) => d.sqlVmResourceId);
  const regions = resourcesData.map((d) => d.resource.region);
  const storageAccountNames = resourcesData.map((d) => d.storageAccountName);
  const subnetIds = resourcesData.map((d) => d.subnetId);
  const virtualNetworkNames = resourcesData.map((d) => d.virtualNetworkName);
  const subnetNames = resourcesData.map((d) => d.subnetName);

  // Create mapping from subscription+region to storage account
  const subscriptionRegionToStorageAccount = subscriptionRegionKeys.reduce<
    Record<string, string>
  >((acc, key) => {
    const firstResourceInGroup = resourcesBySubscriptionAndRegion[key][0];
    acc[key] = firstResourceInGroup.storageAccountName;
    return acc;
  }, {});

  return `# Create the bash script file
cat > configure-sql-vms.sh << 'SCRIPT_EOF'
#!/bin/bash
set -e  # Exit on any error

echo "🔍 Checking Azure CLI version..."
if ! command -v az &> /dev/null; then
  echo "❌ Azure CLI is not installed. Please install it first:"
  echo "   https://docs.microsoft.com/en-us/cli/azure/install-azure-cli"
  exit 1
fi

AZ_VERSION=$(az --version 2>/dev/null | head -n 1 | tr -s ' ' | cut -d' ' -f2)
REQUIRED_VERSION="2.40.0"

echo "Detected Azure CLI version: $AZ_VERSION"

version_compare() {
  local current_version="$1"
  local required_version="$2"
  
  if [ -z "$current_version" ]; then
    echo "❌ Could not detect Azure CLI version"
    exit 1
  fi
  
  if [ "$(printf '%s\\n%s\\n' "$current_version" "$required_version" | sort -V | head -n1)" = "$required_version" ]; then
    return 0  # current >= required
  else
    return 1  # current < required
  fi
}

if version_compare $AZ_VERSION $REQUIRED_VERSION; then
  echo "✅ Azure CLI version $AZ_VERSION meets requirements (>= $REQUIRED_VERSION)"
else
  echo "❌ Azure CLI version $AZ_VERSION is too old. Minimum required: $REQUIRED_VERSION"
  echo ""
  echo "📋 Update commands (choose one):"
  echo "   macOS (Homebrew):     brew update && brew upgrade azure-cli"
  echo "   Ubuntu/Debian:        curl -sL https://aka.ms/InstallAzureCLIDeb | sudo bash"
  echo "   RHEL/CentOS/Fedora:   sudo dnf install -y azure-cli"
  echo "   Windows:              Download from https://aka.ms/installazurecliwindows"
  echo ""
  exit 1
fi

echo "🔧 Checking storage-preview extension..."
if ! az extension show -n storage-preview &>/dev/null; then
  echo "📦 Installing storage-preview extension..."
  az extension add -n storage-preview --only-show-errors
else
  echo "📦 Updating storage-preview extension..."
  az extension update -n storage-preview --only-show-errors
fi

# Arrays for all resources
SUBSCRIPTION_IDS=(${subscriptionIds.map((id) => `"${id}"`).join(' ')})
CONTAINER_NAMES=(${containerNames.map((name) => `"${name}"`).join(' ')})
STORAGE_ACCOUNT_RESOURCE_GROUPS=(${storageAccountResourceGroups.map((rg) => `"${rg}"`).join(' ')})
SQL_VM_RESOURCE_GROUPS=(${sqlVmResourceGroups.map((rg) => `"${rg}"`).join(' ')})
SQL_VM_NAMES=(${sqlVmNames.map((name) => `"${name}"`).join(' ')})
SQL_VM_RESOURCE_IDS=(${sqlVmResourceIds.map((id) => `"${id}"`).join(' ')})
REGIONS=(${regions.map((region) => `"${region}"`).join(' ')})
STORAGE_ACCOUNT_NAMES=(${storageAccountNames.map((name) => `"${name}"`).join(' ')})
SUBNET_IDS=(${subnetIds.map((id) => `"${id}"`).join(' ')})
VIRTUAL_NETWORK_NAMES=(${virtualNetworkNames.map((name) => `"${name}"`).join(' ')})
SUBNET_NAMES=(${subnetNames.map((name) => `"${name}"`).join(' ')})

# Get unique subscription+region combinations for storage account creation
SUBSCRIPTION_REGION_KEYS=(${subscriptionRegionKeys.map((key) => `"${key}"`).join(' ')})

# Subscription+region to storage account mapping
declare -A SUBSCRIPTION_REGION_TO_STORAGE_ACCOUNT
${Object.entries(subscriptionRegionToStorageAccount)
  .map(
    ([key, storageAccountName]) =>
      `SUBSCRIPTION_REGION_TO_STORAGE_ACCOUNT["${key}"]="${storageAccountName}"`
  )
  .join('\n')}

# Resource count
RESOURCE_COUNT=${resourcesData.length}

echo "Configuring $RESOURCE_COUNT SQL VM(s) across ${subscriptionRegionKeys.length} subscription+region combination(s)"

# Step 1: Set up network configuration for each SQL VM and configure service endpoints
echo "🔗 Setting up service endpoints using pre-computed network configuration..."
declare -A DISCOVERED_VNETS
declare -A DISCOVERED_SUBNETS

for i in $(seq 0 $((RESOURCE_COUNT-1))); do
  echo "Using network configuration for SQL VM \${SQL_VM_NAMES[i]} ($(($i+1))/$RESOURCE_COUNT)"

  # Use pre-computed network information
  VNET_NAME=\${VIRTUAL_NETWORK_NAMES[i]}
  SUBNET_NAME=\${SUBNET_NAMES[i]}
  SUBNET_ID=\${SUBNET_IDS[i]}

  # Extract VNet resource group from the subnet ID
  VNET_RG=$(echo $SUBNET_ID | sed 's|.*/resourceGroups/\\([^/]*\\)/.*|\\1|')

  echo "  📍 VM \${SQL_VM_NAMES[i]} is in VNet: $VNET_NAME, Subnet: $SUBNET_NAME, RG: $VNET_RG"

  # Store network information for service endpoint configuration
  DISCOVERED_VNETS["\${SUBSCRIPTION_IDS[i]}|$VNET_RG|$VNET_NAME"]="1"
  DISCOVERED_SUBNETS["\${SUBSCRIPTION_IDS[i]}|$VNET_RG|$VNET_NAME|$SUBNET_NAME"]="1"
done

# Configure service endpoints for unique subnets
echo "🔗 Configuring Microsoft.Storage service endpoints for discovered subnets..."
for SUBNET_KEY in "\${!DISCOVERED_SUBNETS[@]}"; do
  IFS='|' read -r SUB_ID VNET_RG VNET_NAME SUBNET_NAME <<< "$SUBNET_KEY"
  echo "  📡 Adding Microsoft.Storage service endpoint to subnet $SUBNET_NAME in VNet $VNET_NAME"

  az account set --subscription $SUB_ID

  # Get existing service endpoints
  EXISTING_ENDPOINTS=$(az network vnet subnet show --name $SUBNET_NAME --vnet-name $VNET_NAME --resource-group $VNET_RG --query "serviceEndpoints[].service" -o tsv | tr '\\n' ' ')

  # Check if Microsoft.Storage is already configured
  if [[ $EXISTING_ENDPOINTS != *"Microsoft.Storage"* ]]; then
    # Add Microsoft.Storage to existing endpoints
    ALL_ENDPOINTS="Microsoft.Storage"
    if [ ! -z "$EXISTING_ENDPOINTS" ]; then
      ALL_ENDPOINTS="$EXISTING_ENDPOINTS Microsoft.Storage"
    fi

    az network vnet subnet update \\
      --name $SUBNET_NAME \\
      --vnet-name $VNET_NAME \\
      --resource-group $VNET_RG \\
      --service-endpoints $ALL_ENDPOINTS \\
      --only-show-errors

    echo "  ✅ Added Microsoft.Storage service endpoint to subnet $SUBNET_NAME"
  else
    echo "  ℹ️  Microsoft.Storage service endpoint already exists for subnet $SUBNET_NAME"
  fi
done

# Step 2: Create storage accounts (one per unique subscription+region combination)
for SUB_REGION_KEY in "\${SUBSCRIPTION_REGION_KEYS[@]}"; do
  IFS='|' read -r SUB_ID REGION <<< "$SUB_REGION_KEY"
  echo "Processing subscription: $SUB_ID, region: $REGION"
  az account set --subscription $SUB_ID

  # Find first resource in this subscription+region combination to get resource group
  for i in $(seq 0 $((RESOURCE_COUNT-1))); do
    if [ "\${SUBSCRIPTION_IDS[i]}" = "$SUB_ID" ] && [ "\${REGIONS[i]}" = "$REGION" ]; then
      # Get storage account name from mapping
      STORAGE_NAME=\${SUBSCRIPTION_REGION_TO_STORAGE_ACCOUNT["$SUB_REGION_KEY"]}
      echo "Creating storage account $STORAGE_NAME for subscription $SUB_ID in region $REGION"

      az storage account create \\
        --name $STORAGE_NAME \\
        --resource-group "\${STORAGE_ACCOUNT_RESOURCE_GROUPS[i]}" \\
        --location "$REGION" \\
        --sku Standard_LRS \\
        --public-network-access Enabled \\
        --tags "eon:storage-role=sql-vm-pitr" "eon:eon-account-id=${eonAccountId}" \\
        --min-tls-version TLS1_2 \\
        --only-show-errors

      break
    fi
  done
done

# Step 3: Create blob containers (before network restrictions)
echo "📦 Creating blob containers for SQL VMs..."
# Ensure required Azure CLI extension for management-plane container commands
if ! az storage container-rm -h >/dev/null 2>&1; then
  az extension add -n storage-preview --only-show-errors
fi
for i in $(seq 0 $((RESOURCE_COUNT-1))); do
  echo "Creating container for SQL VM \${SQL_VM_NAMES[i]} ($(($i+1))/$RESOURCE_COUNT)"

  az account set --subscription \${SUBSCRIPTION_IDS[i]}

  # Use the pre-computed storage account name for this resource
  STORAGE_NAME=\${STORAGE_ACCOUNT_NAMES[i]}

  # Create blob container for this SQL VM
  az storage container-rm show \\
    --name \${CONTAINER_NAMES[i]} \\
    --storage-account $STORAGE_NAME \\
    --resource-group \${STORAGE_ACCOUNT_RESOURCE_GROUPS[i]} \\
    --only-show-errors >/dev/null 2>&1 || \\
  az storage container-rm create \\
    --name \${CONTAINER_NAMES[i]} \\
    --storage-account $STORAGE_NAME \\
    --resource-group \${STORAGE_ACCOUNT_RESOURCE_GROUPS[i]} \\
    --public-access off \\
    --only-show-errors

  echo "  ✅ Container \${CONTAINER_NAMES[i]} created in storage account $STORAGE_NAME"
done

# Step 4: Configure storage account network rules (after containers are created)
echo "🔒 Configuring storage account network security..."
for SUB_REGION_KEY in "\${SUBSCRIPTION_REGION_KEYS[@]}"; do
  IFS='|' read -r SUB_ID REGION <<< "$SUB_REGION_KEY"
  az account set --subscription $SUB_ID

  # Get storage account name from mapping
  STORAGE_NAME=\${SUBSCRIPTION_REGION_TO_STORAGE_ACCOUNT["$SUB_REGION_KEY"]}

  # Find the storage account resource group for this subscription+region
  for i in $(seq 0 $((RESOURCE_COUNT-1))); do
    if [ "\${SUBSCRIPTION_IDS[i]}" = "$SUB_ID" ] && [ "\${REGIONS[i]}" = "$REGION" ]; then
      STORAGE_ACCOUNT_RG="\${STORAGE_ACCOUNT_RESOURCE_GROUPS[i]}"
      break
    fi
  done

  echo "  🛡️  Configuring network rules for storage account $STORAGE_NAME"

  # Set default action to deny and disable ALL bypass exceptions for maximum security
  az storage account update \\
    --name $STORAGE_NAME \\
    --resource-group $STORAGE_ACCOUNT_RG \\
    --default-action Deny \\
    --bypass None \\
    --only-show-errors

  # Add subnet rules for all SQL VMs in this subscription+region
  for i in $(seq 0 $((RESOURCE_COUNT-1))); do
    if [ "\${SUBSCRIPTION_IDS[i]}" = "$SUB_ID" ] && [ "\${REGIONS[i]}" = "$REGION" ]; then
      SUBNET_ID=\${SUBNET_IDS[i]}
      echo "    🔗 Adding subnet access rule for SQL VM \${SQL_VM_NAMES[i]}"

      az storage account network-rule add \\
        --account-name $STORAGE_NAME \\
        --resource-group $STORAGE_ACCOUNT_RG \\
        --subnet $SUBNET_ID \\
        --only-show-errors
    fi
  done

  echo "  ✅ Network rules configured for storage account $STORAGE_NAME"
done

# Step 5: Deploy ARM templates in parallel for all SQL VMs
echo "🚀 Starting parallel deployment of $RESOURCE_COUNT SQL VM(s)..."

for i in $(seq 0 $((RESOURCE_COUNT-1))); do
  {
    echo "⏳ [$(date '+%H:%M:%S')] Starting deployment for SQL VM \${SQL_VM_NAMES[i]} ($(($i+1))/$RESOURCE_COUNT)"

    az account set --subscription \${SUBSCRIPTION_IDS[i]}

    # Use the pre-computed storage account name for this resource
    STORAGE_NAME=\${STORAGE_ACCOUNT_NAMES[i]}

    # Find the storage account resource group for this SQL VM's subscription+region
    STORAGE_ACCOUNT_RG=""
    for j in $(seq 0 $((RESOURCE_COUNT-1))); do
      if [ "\${SUBSCRIPTION_IDS[j]}" = "\${SUBSCRIPTION_IDS[i]}" ] && [ "\${REGIONS[j]}" = "\${REGIONS[i]}" ]; then
        STORAGE_ACCOUNT_RG="\${STORAGE_ACCOUNT_RESOURCE_GROUPS[j]}"
        break
      fi
    done

    # Create unique ARM template file for this SQL VM
    VM_HASH=$(echo "\${SQL_VM_RESOURCE_IDS[i]}" | cksum | cut -d' ' -f1)
    TEMPLATE_FILE="sql-vm-template-$VM_HASH.json"
    cat > $TEMPLATE_FILE << 'TEMPLATE_EOF'
{
"$schema": "https://schema.management.azure.com/schemas/2019-04-01/deploymentTemplate.json#",
"contentVersion": "1.0.0.0",
"parameters": {
  "location": { "type": "string" },
  "virtualMachineName": { "type": "string" },
  "storageAccountName": { "type": "string" },
  "storageContainerName": { "type": "string" },
  "storageAccountResourceGroupName": { "type": "string" }
},
"resources": [
  {
    "type": "Microsoft.SqlVirtualMachine/SqlVirtualMachines",
    "apiVersion": "2022-07-01-preview",
    "name": "[parameters('virtualMachineName')]",
    "location": "[parameters('location')]",
    "properties": {
      "virtualMachineResourceId": "[resourceId('Microsoft.Compute/virtualMachines', parameters('virtualMachineName'))]",
      "AutoBackupSettings": {
        "Enable": true,
        "RetentionPeriod": "7",
        "StorageAccountUrl": "[reference(resourceId(parameters('storageAccountResourceGroupName'), 'Microsoft.Storage/storageAccounts', parameters('storageAccountName')), '2019-06-01').primaryEndpoints.blob]",
        "StorageAccessKey": "[first(listKeys(resourceId(parameters('storageAccountResourceGroupName'), 'Microsoft.Storage/storageAccounts', parameters('storageAccountName')), '2019-06-01').keys).value]",
        "StorageContainerName": "[parameters('storageContainerName')]",
        "EnableEncryption": false,
        "BackupSystemDbs": true,
        "BackupScheduleType": "Manual",
        "FullBackupFrequency": "Daily",
        "FullBackupStartTime": "0",
        "FullBackupWindowHours": "4",
        "LogBackupFrequency": "5"
      }
    }
  }
]
}
TEMPLATE_EOF

    az deployment group create \\
      --resource-group \${SQL_VM_RESOURCE_GROUPS[i]} \\
      --template-file $TEMPLATE_FILE \\
      --parameters \\
        location="\${REGIONS[i]}" \\
        virtualMachineName="\${SQL_VM_NAMES[i]}" \\
        storageAccountName="$STORAGE_NAME" \\
        storageContainerName="\${CONTAINER_NAMES[i]}" \\
        storageAccountResourceGroupName="$STORAGE_ACCOUNT_RG" \\
      --mode Incremental \\
      --only-show-errors

    # Clean up the template file for this VM
    rm $TEMPLATE_FILE

    echo "✅ [$(date '+%H:%M:%S')] Completed deployment for SQL VM \${SQL_VM_NAMES[i]}"
  } &
done

echo "⏳ Waiting for all ARM template deployments to complete..."
wait

echo "🎉 Configuration complete for all $RESOURCE_COUNT SQL VM(s) - deployed in parallel!"

SCRIPT_EOF

# Make it executable and run with bash
chmod +x configure-sql-vms.sh
bash configure-sql-vms.sh

# Clean up the script file
rm configure-sql-vms.sh`;
};

// Generate Terraform script using pre-computed network information from GetSqlVmNetworkInfo API
// This eliminates the need for azurerm_virtual_machine and azurerm_network_interface data sources
const generateTerraformScript = (
  resourcesData: SqlVmResourceData[],
  eonAccountId: string
): string => {
  // Convert resources data to Terraform format
  const resourcesForTerraform = resourcesData.map((data, index) => ({
    key: `resource_${index}`,
    subscription_id: data.resource.providerAccountId,
    storage_account_resource_group:
      data.storageAccountResourceGroup || '<storage-account-resource-group>',
    sql_vm_resource_group:
      data.resource.resourceGroupName || '<sql-vm-resource-group>',
    vm_name: data.resource.resourceName || '<sql-vm-name>',
    region: data.resource.region,
    storage_account: data.storageAccountName,
    container_name: data.containerName,
    sql_vm_resource_id: data.sqlVmResourceId,
    subnet_id: data.subnetId,
    vnet_name: data.virtualNetworkName,
    subnet_name: data.subnetName,
  }));

  // Group by subscription+region for storage accounts
  const storageAccountsBySubscriptionRegion = resourcesData.reduce<
    Record<
      string,
      {
        name: string;
        resource_group: string;
        region: string;
      }
    >
  >((acc, data) => {
    const key = `${data.resource.providerAccountId}|${data.resource.region}`;
    if (!acc[key]) {
      acc[key] = {
        name: data.storageAccountName,
        resource_group:
          data.storageAccountResourceGroup ||
          '<storage-account-resource-group>',
        region: data.resource.region,
      };
    }
    return acc;
  }, {});

  return `terraform {
  required_providers {
    azurerm = {
      source  = "hashicorp/azurerm"
      version = "~>3.0"
    }
    azapi = {
      source  = "azure/azapi"
      version = "~>1.0"
    }
  }
}

# Configure the Microsoft Azure Provider
provider "azurerm" {
  features {}
}

# Configure the Azure API Provider
provider "azapi" {}

locals {
  # Resource configuration
  sql_vm_resources = {
${resourcesForTerraform
  .map(
    (r) => `    "${r.key}" = {
      subscription_id                = "${r.subscription_id}"
      storage_account_resource_group = "${r.storage_account_resource_group}"
      sql_vm_resource_group          = "${r.sql_vm_resource_group}"
      vm_name                        = "${r.vm_name}"
      region                         = "${r.region}"
      storage_account                = "${r.storage_account}"
      container_name                 = "${r.container_name}"
      sql_vm_resource_id             = "${r.sql_vm_resource_id}"
      subnet_id                      = "${r.subnet_id}"
      vnet_name                      = "${r.vnet_name}"
      subnet_name                    = "${r.subnet_name}"
    }`
  )
  .join('\n')}
  }

  # Group by subscription+region for storage accounts
  storage_accounts = {
${Object.entries(storageAccountsBySubscriptionRegion)
  .map(
    ([key, account]) => `    "${key}" = {
      name           = "${account.name}"
      resource_group = "${account.resource_group}"
      region         = "${account.region}"
    }`
  )
  .join('\n')}
  }

  # Create unique subnet identifiers for service endpoint configuration
  unique_subnets = {
${(() => {
  const uniqueSubnets = resourcesData.reduce<
    Record<
      string,
      {
        subscription_id: string;
        resource_group: string;
        vnet_name: string;
        subnet_name: string;
      }
    >
  >((acc, data) => {
    const key = `${data.resource.providerAccountId}|${data.resource.resourceGroupName}|${data.virtualNetworkName}|${data.subnetName}`;
    if (!acc[key]) {
      acc[key] = {
        subscription_id: data.resource.providerAccountId,
        resource_group:
          data.resource.resourceGroupName || '<sql-vm-resource-group>',
        vnet_name: data.virtualNetworkName,
        subnet_name: data.subnetName,
      };
    }
    return acc;
  }, {});

  return Object.entries(uniqueSubnets)
    .map(
      ([key, subnet]) => `    "${key}" = {
      subscription_id = "${subnet.subscription_id}"
      resource_group  = "${subnet.resource_group}"
      vnet_name      = "${subnet.vnet_name}"
      subnet_name    = "${subnet.subnet_name}"
    }`
    )
    .join('\n');
})()}
  }
}

# Get current subscription info
data "azurerm_client_config" "current" {}

# Get subnet information for existing configuration (needed for address_prefixes and current endpoints)
data "azurerm_subnet" "sql_vm_subnet" {
  for_each = local.unique_subnets

  name                 = each.value.subnet_name
  virtual_network_name = each.value.vnet_name
  resource_group_name  = each.value.resource_group
}

# Update subnets to include Microsoft.Storage service endpoint
resource "azurerm_subnet" "sql_vm_subnet_with_endpoint" {
  for_each = local.unique_subnets

  name                 = each.value.subnet_name
  resource_group_name  = each.value.resource_group
  virtual_network_name = each.value.vnet_name
  address_prefixes     = data.azurerm_subnet.sql_vm_subnet[each.key].address_prefixes

  # Preserve existing service endpoints and add Microsoft.Storage
  service_endpoints = distinct(concat(
    data.azurerm_subnet.sql_vm_subnet[each.key].service_endpoints,
    ["Microsoft.Storage"]
  ))

  # Preserve any existing delegations
  dynamic "delegation" {
    for_each = try(data.azurerm_subnet.sql_vm_subnet[each.key].delegation, [])
    content {
      name = delegation.value.name
      service_delegation {
        name    = delegation.value.service_delegation[0].name
        actions = delegation.value.service_delegation[0].actions
      }
    }
  }
}

# Storage accounts (one per subscription+region combination)
resource "azurerm_storage_account" "sql_vm_backup" {
  for_each = local.storage_accounts

  name                     = each.value.name
  resource_group_name      = each.value.resource_group
  location                 = each.value.region
  account_tier             = "Standard"
  account_replication_type = "LRS"
  public_network_access_enabled = true
  min_tls_version = "TLS1_2"

  tags = {
    "eon:storage-role"   = "sql-vm-pitr"
    "eon:eon-account-id" = "${eonAccountId}"
  }

  # Configure network access rules
  network_rules {
    default_action = "Deny"
    bypass         = ["None"]  # Disable ALL Azure service exceptions for maximum security

    # Allow access from SQL VM subnets
    virtual_network_subnet_ids = [
      for resource_key, resource in local.sql_vm_resources :
      azurerm_subnet.sql_vm_subnet_with_endpoint["\${resource.subscription_id}|\${resource.sql_vm_resource_group}|\${resource.vnet_name}|\${resource.subnet_name}"].id
      if "\${resource.subscription_id}|\${resource.region}" == each.key
    ]
  }

  depends_on = [azurerm_subnet.sql_vm_subnet_with_endpoint]

  lifecycle {
    prevent_destroy = true
  }
}

# Create blob containers (one per SQL VM)
resource "azapi_resource" "sql_vm_backup_container" {
  for_each = local.sql_vm_resources

  type      = "Microsoft.Storage/storageAccounts/blobServices/containers@2022-09-01"
  name      = each.value.container_name
  parent_id = "\${azurerm_storage_account.sql_vm_backup["\${each.value.subscription_id}|\${each.value.region}"].id}/blobServices/default"

  body = jsonencode({
    properties = {
      publicAccess = "None"
    }
  })

  depends_on = [azurerm_storage_account.sql_vm_backup]
}

# Reference existing VMs
data "azurerm_virtual_machine" "existing_vm" {
  for_each = local.sql_vm_resources

  name                = each.value.vm_name
  resource_group_name = each.value.sql_vm_resource_group
}

# Configure SQL VM auto backup using Azure API directly
resource "azapi_resource" "sql_vm_autobackup" {
  for_each = local.sql_vm_resources

  type      = "Microsoft.SqlVirtualMachine/sqlVirtualMachines@2022-07-01-preview"
  name      = each.value.vm_name
  parent_id = "/subscriptions/\${each.value.subscription_id}/resourceGroups/\${each.value.sql_vm_resource_group}"

  body = jsonencode({
    location = each.value.region
    properties = {
      virtualMachineResourceId = data.azurerm_virtual_machine.existing_vm[each.key].id
      autoBackupSettings = {
        enable                = true
        retentionPeriod      = 7
        storageAccountUrl    = azurerm_storage_account.sql_vm_backup["\${each.value.subscription_id}|\${each.value.region}"].primary_blob_endpoint
        storageAccessKey     = azurerm_storage_account.sql_vm_backup["\${each.value.subscription_id}|\${each.value.region}"].primary_access_key
        storageContainerName = each.value.container_name
        enableEncryption     = false
        backupSystemDbs      = true
        backupScheduleType   = "Manual"
        fullBackupFrequency  = "Daily"
        fullBackupStartTime  = 0
        fullBackupWindowHours = 4
        logBackupFrequency   = 5
      }
    }
  })

  depends_on = [
    azapi_resource.sql_vm_backup_container,
  ]
}`;
};

const generateStorageAccountName = async (
  eonAccountId: string,
  subscriptionId: string,
  region: string
) => {
  const eonIdSuffix = eonAccountId.substring(0, 6) || 'xxxxxx';
  const combinedInput = `${subscriptionId}|${region}`;
  const hashHex = await sha256Hex(combinedInput);
  const hashSuffix = hashHex.substring(0, 10);
  return `eonsqlbk${eonIdSuffix}${hashSuffix}`;
};

const buildSqlVmResourceId = (resource: InventoryResource): string => {
  return `/subscriptions/${resource.providerAccountId}/resourceGroups/${resource.resourceGroupName}/providers/Microsoft.SqlVirtualMachine/SqlVirtualMachines/${resource.resourceName}`;
};

const generateStableContainerName = async (
  resource: InventoryResource
): Promise<string> => {
  const stableInput = `${resource.providerAccountId}-${resource.resourceGroupName}-${resource.resourceName}`;

  const hashId = (await sha256Hex(stableInput)).substring(0, 12);

  // Format: sqlvm-backup-{hash12chars}
  // Starts with letter, ends with alphanumeric, single hyphens only
  return `sqlvm-backup-${hashId}`;
};

const sha256Hex = async (str: string): Promise<string> => {
  const data = new TextEncoder().encode(str);
  const buf = await crypto.subtle.digest('SHA-256', data);
  return [...new Uint8Array(buf)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

const getSourceAccountForResource = (
  resource: InventoryResource,
  sourceAccounts?: SourceAccount[]
): SourceAccount | undefined => {
  if (!sourceAccounts) return undefined;
  return sourceAccounts.find(
    (account) => account.providerAccountId === resource.providerAccountId
  );
};

const getResourceGroupName = (
  resource: InventoryResource,
  sourceAccount?: SourceAccount
): string => {
  // Use eon internal resource group if available
  if (
    sourceAccount?.sourceAccountAttributes?.azure?.eonResourceGroupName?.trim()
  ) {
    return sourceAccount.sourceAccountAttributes.azure.eonResourceGroupName;
  }
  // Fallback to SQL VM's resource group (current behavior)
  return resource.resourceGroupName || '';
};
